import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATHS } from 'routes/route-paths';

const isModuleFederation =
  typeof process !== 'undefined' && process.env.IS_MODULE_FEDERATION === 'true';

const getAccountDetailRoute = (
  companyId: number,
  projectId?: number,
  isModuleFed?: boolean,
) => {
  // If isModuleFed is true, use accounts/nitro-pages route
  if (isModuleFed) {
    return `/${ROUTE_PATHS.ACCOUNTS}/${companyId}/nitro-pages`;
  }

  // If projectId is provided (module federation mode), use project routes
  if (projectId != null || isModuleFederation) {
    const id = projectId ?? companyId;

    return `/projects/${id}/nitro-pages`;
  }

  return `/${ROUTE_PATHS.ACCOUNTS}/${ROUTE_PATHS.ACCOUNT_DETAIL.replace(
    ':accountId',
    companyId.toString() ?? '',
  )}`;
};
const getBaseSpacesRoute = (
  companyId: number,
  spaceDocumentId?: number,
  projectId?: number,
  isModuleFed?: boolean,
) => {
  const baseRoute = getAccountDetailRoute(companyId, projectId, isModuleFed);

  // If isModuleFed is true, use accounts/nitro-pages/{id} route
  if (isModuleFed) {
    return spaceDocumentId ? `${baseRoute}/${spaceDocumentId}` : baseRoute;
  }

  // If projectId is provided (module federation mode), use project routes
  if (projectId != null || isModuleFederation) {
    return spaceDocumentId ? `${baseRoute}/${spaceDocumentId}` : baseRoute;
  }

  const spacesPath = spaceDocumentId
    ? ROUTE_PATHS.ACCOUNT_DETAIL_SPACES.replace(
        ':spaceDocumentId?',
        spaceDocumentId.toString(),
      )
    : ROUTE_PATHS.ACCOUNT_DETAIL_SPACES.replace('/:spaceDocumentId?', '');

  return `${baseRoute}/${spacesPath}`;
};
const getSpacesRoute = (
  companyId: number,
  projectId?: number,
  isModuleFed?: boolean,
) => {
  return getBaseSpacesRoute(companyId, undefined, projectId, isModuleFed);
};
const getSpaceDocumentRoute = (
  companyId: number,
  meetingId: number,
  projectId?: number,
  isModuleFed?: boolean,
) => {
  return getBaseSpacesRoute(companyId, meetingId, projectId, isModuleFed);
};
const getSpaceDocumentEditRoute = (
  companyId: number,
  meetingId: number,
  projectId?: number,
  isModuleFed?: boolean,
) => {
  // If isModuleFed is true, use accounts/{id}/nitro-pages/{id}/edit route
  if (isModuleFed) {
    return `/${ROUTE_PATHS.ACCOUNTS}/${companyId}/nitro-pages/${meetingId}/edit`;
  }

  const baseRoute = getSpaceDocumentRoute(
    companyId,
    meetingId,
    projectId,
    isModuleFed,
  );

  // If projectId is provided (module federation mode), use project routes
  if (projectId != null || isModuleFederation) {
    return `${baseRoute}/edit`;
  }

  return `${baseRoute}/${ROUTE_PATHS.ACCOUNT_DETAIL_MEETING_EDIT}`;
};
const getSpaceDocumentNewRoute = (
  companyId: number,
  projectId?: number,
  isModuleFed?: boolean,
) => {
  // If isModuleFed is true, use accounts/{id}/nitro-pages/new route
  if (isModuleFed) {
    return `/${ROUTE_PATHS.ACCOUNTS}/${companyId}/nitro-pages/new`;
  }

  const baseRoute = getAccountDetailRoute(companyId, projectId, isModuleFed);

  // If projectId is provided (module federation mode), use project routes
  if (projectId != null || isModuleFederation) {
    return `${baseRoute}/new`;
  }

  return `${baseRoute}/spaces/${ROUTE_PATHS.NEW}`;
};

export const useAccountSpacesRouteNavigations = (
  projectId?: number,
  isModuleFed?: boolean,
) => {
  const navigate = useNavigate();
  const navigateToSpaces = useCallback(
    (companyId: number) => {
      navigate(getSpacesRoute(companyId, projectId, isModuleFed));
    },
    [navigate, projectId, isModuleFed],
  );
  const navigateToSpaceDocument = useCallback(
    (companyId: number, meetingId: number) => {
      navigate(
        getSpaceDocumentRoute(companyId, meetingId, projectId, isModuleFed),
      );
    },
    [navigate, projectId, isModuleFed],
  );
  const navigateToSpaceDocumentEdit = useCallback(
    (companyId: number, meetingId: number) => {
      navigate(
        getSpaceDocumentEditRoute(companyId, meetingId, projectId, isModuleFed),
      );
    },
    [navigate, projectId, isModuleFed],
  );
  const navigateToSpaceDocumentNew = useCallback(
    (companyId: number) => {
      navigate(getSpaceDocumentNewRoute(companyId, projectId, isModuleFed));
    },
    [navigate, projectId, isModuleFed],
  );

  return {
    navigateToSpaces,
    navigateToSpaceDocument,
    navigateToSpaceDocumentEdit,
    navigateToSpaceDocumentNew,
  };
};
